import { Popover, Skeleton } from "@mantine/core";
import { useState } from "react";
import { useIntl } from "react-intl";
import { AgencyDetails } from "@/interfaces/agencyDetails";
import { useFetch } from "@/hooks/useFetch";
import { apiRoutes } from "@/routes/apiRoutes";
import { Address } from "@/interfaces/agencyDetails";
import { DisponibilityAgency } from "@/interfaces/disponibilityAgency";
import styles from "./AgencyInformationPopover.module.scss";

type AgencyInformationPopoverProps = {
  agency: DisponibilityAgency;
  targetComponent: (props: {
    agency: DisponibilityAgency;
    isPopoverOpen: boolean;
    togglePopover: () => void;
  }) => JSX.Element;
};

export const isLoadedAgencyDetails = (
  isLoading: boolean,
  agencyInformation?: AgencyDetails
): agencyInformation is AgencyDetails => !isLoading;

export const getGoogleMapsAgencyLink = (agencyAdress: Address) => {
  if (process.env.NEXT_PUBLIC_BASE_MAPS_URL !== undefined) {
    if (agencyAdress.street !== null && agencyAdress.city !== null) {
      return `${process.env.NEXT_PUBLIC_BASE_MAPS_URL}${agencyAdress.street} ${agencyAdress.city}`;
    }

    return `${process.env.NEXT_PUBLIC_BASE_MAPS_URL}${agencyAdress.latitude},${agencyAdress.longitude}`;
  }
};

export const AgencyInformationPopover = ({
  agency,
  targetComponent: TargetComponent,
}: AgencyInformationPopoverProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const intl = useIntl();

  const {
    data: agencyInformation,
    isLoading,
    isError,
  } = useFetch<AgencyDetails, Error>(
    apiRoutes.fetchAgencyInformation(agency.id),
    [agency.id],
    {
      staleTime: Infinity,
    }
  );

  const renderAgencyAddressSwitch = () => {
    if (isError) {
      return (
        <div className={styles.errorPopover}>
          {intl.formatMessage({ id: "popoverError" })}
        </div>
      );
    } else if (!isLoadedAgencyDetails(isLoading, agencyInformation)) {
      return <Skeleton className={styles.loadingPopover} />;
    } else {
      return (
        <>
          <div className={styles.addressSubBlock}>
            <p className={styles.title}>
              {intl.formatMessage({ id: "address" })}
            </p>
            <p className={styles.information}>
              {agencyInformation.addressDto.street}
              <br />
              {agencyInformation.addressDto.zipCode}{" "}
              {agencyInformation.addressDto.city}
            </p>
            <div className={styles.linkContainer}>
              <a
                className={styles.link}
                href={getGoogleMapsAgencyLink(agencyInformation.addressDto)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {intl.formatMessage({ id: "openInGoogleMaps" })}
              </a>
            </div>
          </div>
          <div className={styles.contactSubBlock}>
            <p className={styles.title}>
              {intl.formatMessage({ id: "contact" })}
            </p>
            <p className={styles.information}>
              {agencyInformation.communicationMeansDto.phone !== null && (
                <a
                  href={`tel:${agencyInformation.communicationMeansDto.phone}`}
                >
                  {agencyInformation.communicationMeansDto.phone}
                </a>
              )}
            </p>
            <div className={styles.linkContainer}>
              {agencyInformation.communicationMeansDto.email !== null && (
                <a
                  className={styles.link}
                  href={`mailto:${agencyInformation.communicationMeansDto.email}`}
                >
                  {agencyInformation.communicationMeansDto.email}
                </a>
              )}
            </div>
          </div>
        </>
      );
    }
  };

  return (
    <Popover
      opened={isPopoverOpen}
      onClose={() => setIsPopoverOpen(false)}
      target={
        <TargetComponent
          agency={agency}
          togglePopover={() => setIsPopoverOpen((isOpen) => !isOpen)}
          isPopoverOpen={isPopoverOpen}
        />
      }
      position="bottom"
      placement="start"
      classNames={{ body: styles.popoverBody, inner: styles.popoverInner }}
      zIndex={10}
    >
      <div className={styles.informationContainer}>
        {renderAgencyAddressSwitch()}
      </div>
    </Popover>
  );
};
